import React from 'react';
import { Handle, Position } from 'reactflow';
import { Plus, AlertTriangle, Play } from 'lucide-react';
import { NODE_CATALOG } from 'features/agents/WorkflowEditor/nodeCatalog';
import NodeMark from 'features/agents/WorkflowEditor/NodeMark';
import { formatExecDuration } from 'features/agents/lib/format';
import { execStatusLabel } from 'features/agents/lib/status';
import { useCanvasActions } from 'features/agents/canvas/CanvasActionsContext';

/** Where a node's source handles sit along its bottom edge. */
export function portLeft(index, count) {
  return `${((index + 1) * 100) / (count + 1)}%`;
}

/**
 * A node on the canvas: a neutral tile, its name, and under it one line of
 * what it will do. Nothing else, so a long agent still reads as a list.
 *
 * data: { kind, name, summary, mode: 'edit'|'run', readOnly, invalid,
 *   invalidMsg, status, durationMs, openPorts, ports, labelled, size, showRun }
 *   status      running | succeeded | failed | skipped | cancelled | queued
 *   openPorts   ports with nothing after them: each gets a visible "+" below
 *   showRun     a manual trigger the page can run right now
 *   ports       [{ id, label }] when the node branches (else the catalog's
 *               fixed ports); `labelled` prints each label above its handle
 *               along the bottom (Decide: one per route), `size` is the box
 *
 * Nodes are not dragged; click selects (the page opens settings), Enter does
 * the same from the keyboard. Deleting a step is deliberately not a key:
 * only the trash button in its settings panel does it.
 */
export default function AgentNode({ id, data, selected }) {
  const actions = useCanvasActions();
  const kind = data?.kind;
  const spec = NODE_CATALOG[kind] || {};
  const isTrigger = spec.category === 'trigger';
  const portList = Array.isArray(data?.ports)
    ? data.ports
    : (spec.ports ? spec.ports.map((p) => ({ id: p, label: p })) : null);
  const ports = portList ? portList.map((p) => p.id) : null;
  const labelOf = (p) => (portList || []).find((x) => x.id === p)?.label || p || '';
  const labelled = !!data?.labelled && !!portList && portList.length > 0;
  const typeLabel = spec.displayName || kind;
  const name = data?.name || typeLabel;
  const status = data?.status || null;
  const runView = data?.mode === 'run';
  const canEdit = !runView && !data?.readOnly;
  const invalid = canEdit && !!data?.invalid;
  const duration = status && data?.durationMs != null ? formatExecDuration(data.durationMs) : '';
  const openPorts = canEdit ? (data?.openPorts || []) : [];

  const label = [name, typeLabel !== name ? typeLabel : null, status ? execStatusLabel(status) : null, invalid ? 'needs setup' : null]
    .filter(Boolean).join(', ');
  const title = invalid && data?.invalidMsg ? data.invalidMsg : (spec.description || typeLabel);

  const classes = ['agent-node'];
  if (labelled) classes.push('has-ports');
  if (selected) classes.push('is-selected');
  if (invalid) classes.push('is-invalid');

  const onKeyDown = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); }
  };

  return (
    <div
      className={classes.join(' ')}
      data-status={status || undefined}
      role="button"
      tabIndex={0}
      aria-label={label}
      aria-pressed={!!selected}
      title={title}
      onKeyDown={onKeyDown}
      style={labelled && data?.size ? { width: data.size.w, height: data.size.h } : undefined}
    >
      {invalid && (
        <span className="agent-node-badge" title={data?.invalidMsg || 'Not set up yet'} role="img" aria-label="Needs setup">
          <AlertTriangle size={9} strokeWidth={2.2} />
        </span>
      )}

      {!isTrigger && <Handle type="target" position={Position.Top} className="agent-handle" isConnectable={canEdit} />}

      <NodeMark kind={kind} size={24} />
      <div className="agent-node-text">
        <div className="agent-node-name">{name}</div>
        {data?.summary ? <div className="agent-node-summary">{data.summary}</div> : null}
      </div>

      {status ? (
        <span className="agent-node-status">
          {duration ? <span className="agent-node-duration">{duration}</span> : null}
          <span className="agent-node-dot" data-status={status} aria-hidden="true" />
        </span>
      ) : data?.showRun && canEdit && kind === 'trigger_manual' ? (
        <button
          type="button"
          className="agent-node-run"
          title="Test run"
          aria-label="Test run"
          onClick={(e) => { e.stopPropagation(); actions.onRunTrigger(id); }}
        >
          <Play size={9} strokeWidth={2.2} />
        </button>
      ) : null}

      {labelled ? (
        <div className="agent-node-ports" data-testid="node-ports">
          {portList.map((p, i) => (
            <span
              key={p.id}
              className="agent-node-port"
              style={{ left: portLeft(i, portList.length), maxWidth: `calc(${100 / portList.length}% - 6px)` }}
              title={p.label}
            >
              {p.label}
            </span>
          ))}
        </div>
      ) : null}

      {ports
        ? ports.map((p, i) => <Handle key={p} id={p} type="source" position={Position.Bottom} className="agent-handle" style={{ left: portLeft(i, ports.length) }} isConnectable={canEdit} title={labelOf(p)} />)
        : <Handle type="source" position={Position.Bottom} className="agent-handle" isConnectable={canEdit} />}

      {openPorts.map((p) => {
        const idx = ports ? ports.indexOf(p) : -1;
        const left = ports && idx >= 0 ? portLeft(idx, ports.length) : '50%';
        const labelText = p ? labelOf(p) : '';
        const where = labelled ? 'route' : 'branch';
        return (
          <span key={p || 'out'} className="agent-add" style={{ left }}>
            <span className="agent-add-line" />
            <button
              type="button"
              className="agent-add-btn nodrag nopan"
              title={labelText ? `Add a step on the ${labelText} ${where}` : 'Add a step'}
              aria-label={labelText ? `Add a step on the ${labelText} ${where}` : 'Add a step after this one'}
              onClick={(e) => { e.stopPropagation(); actions.onAddAfter(id, p || null); }}
            >
              <Plus size={11} strokeWidth={2} />
              {labelText && !labelled ? <span>{labelText}</span> : null}
            </button>
          </span>
        );
      })}
    </div>
  );
}
